import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Alert, ListItem } from '@material-library';
import { SupportTicketService } from 'src/app/account/services/support-ticket.service';
import { LoggerService } from 'src/app/core/logger.service';
import { UserService } from 'src/app/core/user.service';
import { ErrCode, BlendEvent, User, PermissionActions } from 'src/app/shared/models';
import { BlendService } from 'src/app/shared/services/blend.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Base64Service } from 'src/app/core/base64.service';
import { requiredWithoutWhitespaces } from './help-dialog.validators';
import { HELP_LINKS } from '../../../shared.constants';
import { noop } from 'rxjs';

interface Fields {
  subject: string;
  description: string;
  attachLogs: boolean;
  name: string;
  email: string;
}

type FormControls<T> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

type Field = keyof Fields;

@Component({
  selector: 'app-help-dialog',
  templateUrl: './help-dialog.component.html',
  styleUrls: ['./help-dialog.component.scss'],
})
export class HelpDialogComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public openHelpDialog = false;
  @Input() public openContactSupportDialog = false;
  @Output() public readonly openHelpDialogChange = new EventEmitter<boolean>();

  constructor(
    private loggerService: LoggerService,
    private userService: UserService,
    public blendService: BlendService,
    private supportTicketService: SupportTicketService,
    private fb: FormBuilder,
    private base64: Base64Service,
  ) {}

  public viewContactSupport = false;
  public showThankYouTemplate = false; // Flag to show thank you template
  public helpCategories: ListItem[] = [];
  public email = '';
  public displayName: string;
  public errcode: ErrCode;
  public formValues?: { [k: string]: any };
  public user: User | any;
  public isSupportSent = false; // Flag to track successful send
  public alertsConfig: { [key: string]: Alert } = {};
  public supportForm: FormGroup<FormControls<Fields>>;
  public submitted = false;
  public helpLinks = HELP_LINKS;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['openHelpDialog'] && changes['openHelpDialog']['currentValue'] && this.openContactSupportDialog) {
      this.closeHelpDialog();
      this.viewContactSupport = true;
    }
  }

  public ngOnInit(): void {
    this.user = this.userService.getUser();
    this.initHelpCategories();
    this.initForm();
    this.initAlerts();
  }

  public initAlerts(): void {
    this.alertsConfig.thankYouBanner = {
      type: 'promo',
      showLeadIcon: true,
      leadIcon: 'info',
      title: `Thank you for your message.`,
      description: `Your support request has been successfully sent.`,
      showActions: true,
      showCloseButton: false,
      showSecondaryAction: false,
      secondaryButtonConfig: {
        label: '',
      },
      showPrimaryAction: false,
      primaryButtonConfig: {
        label: '',
        onClick: noop,
      },
    };
  }

  public initHelpCategories = (): void => {
    const isAdmin = this.userService.checkPermission(PermissionActions.MANAGE_BILLING);
    this.helpCategories = [
      {
        text: 'Web Panel overview',
        url: HELP_LINKS.WEB_PANEL_OVERVIEW,
      },
      {
        text: 'Sharing files and folders with links',
        url: HELP_LINKS.SHARING_FOLDERS,
      },
      {
        text: 'Account and billing FAQ',
        url: HELP_LINKS.ACCOUNT_BILLING_FAQ,
      },
      isAdmin &&
        this.user.plan_sku?.startsWith('BPTSP') && {
          text: 'Pro Teams Standard getting started guide',
          url: HELP_LINKS.PRO_TEAM_STANDARD_ADMIN,
        },
      isAdmin &&
        this.user.plan_sku?.startsWith('BPTUP') && {
          text: 'Pro Teams+ Unlimited getting started guide ',
          url: HELP_LINKS.PRO_TEAM_UNLIMITED_ADMIN,
        },
    ]
      .filter(Boolean)
      .map((item) => {
        return {
          primaryText: item.text,
          divider: true,
          isInteractive: true,
          onClick: () => window.open(item.url),
          trail2AdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconType: 'primary',
            iconName: 'chevron-right',
          },
        };
      }) as ListItem[];
  };

  public closeHelpDialog = (): void => {
    this.openHelpDialog = false;
    this.submitted = false;
    this.openHelpDialogChange.emit(this.openHelpDialog);
  };

  public onContactSupportClick = (): void => {
    this.closeHelpDialog();
    window.open(this.helpLinks.CONTACT_SUPPORT, '_blank');
    this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'Contact support' });
  };

  public cleanupSupportState = (): void => {
    this.resetForm();
    this.viewContactSupport = false;
  };

  public onCancelSupportClick = (): void => {
    this.cleanupSupportState();
    this.blendService.track(BlendEvent.CANCEL_HELP);
  };

  public initForm(): void {
    this.supportForm = this.fb.group({
      subject: ['', [Validators.required, requiredWithoutWhitespaces]],
      description: ['', [Validators.required, requiredWithoutWhitespaces]],
      attachLogs: [true],
      name: [this.base64.decode(this.user?.display_name || '') || '', Validators.required],
      email: [this.user?.email || '', Validators.required],
    });
  }

  public resetForm = (): void => {
    this.initForm();
    this.formValues = {
      requester: this.user?.email ?? null,
      name: this.user?.first_name ?? null,
      subject: '',
      description: '',
      attachLogs: true,
    };
  };

  public handleClose = (): void => {
    this.resetForm();
    this.isSupportSent = false;
    this.viewContactSupport = false;
    this.showThankYouTemplate = false; // Reset the thank you template flag
  };

  public getFieldError(fieldName: Field): string {
    const control = this.supportForm?.get(fieldName);
    if (!control?.touched) return '';

    if (control.errors?.required) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} cannot be empty.`;
    }
    if (control.errors?.maxlength) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} cannot exceed ${control.errors.maxlength.requiredLength} characters.`;
    }
    return '';
  }

  public sendSupportTicket = async (): Promise<void> => {
    this.submitted = true;
    if (this.supportForm.invalid) {
      Object.keys(this.supportForm.controls).forEach((key) => {
        const control = this.supportForm.get(key);
        if (control.invalid) {
          control.markAsTouched();
        }
      });
      return;
    }

    try {
      const formValue = this.supportForm.value;
      const logs = formValue.attachLogs ? this.loggerService.dumpLogsText('all') : 'Not provided\n';
      const description = [
        'USER ID: ',
        this.user.uid,
        '--------',
        'Customer wrote:',
        formValue.description,
        '--------',
        'Javascript logging:',
        logs,
        '--------',
      ].join('\n\n');
      const postData = {
        z_requester: this.user.email,
        z_name: this.user.first_name,
        z_subject: formValue.subject,
        z_description: description,
        cpversion: '4.0',
      };

      this.isSupportSent = true;
      await this.supportTicketService.sendSupportTicket(postData);
      this.blendService.track(BlendEvent.CONTACT_SUPPORT, { logs: formValue.attachLogs });
    } catch (error) {
      throw new Error('Network error');
    }
  };

  public mpSyncKnowledgebase(): void {
    this.blendService.track(BlendEvent.SYNC_KNOWLEDGEBASE);
  }
  public ngOnDestroy(): void {
    this.closeHelpDialog();
    this.cleanupSupportState();
  }
}
